import { faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { AnimatePresence } from 'motion/react'
import { type ReactElement, useState } from 'react'
import { createPortal } from 'react-dom'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { themeClass } from '~/styles/theme.css'
import {
  type ApplicationsFilters,
  useApplications
} from '../queries/useApplications'
import { ApplicationCard } from './ApplicationCard'
import { ApplicationModal } from './ApplicationModal'
import { resultsWrapper } from './ApplicationsList.css'

type ApplicationsListProps = {
  filters?: ApplicationsFilters
  filtersLoading?: boolean
}

export const ApplicationsList = ({
  filters,
  filtersLoading
}: ApplicationsListProps): ReactElement => {
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    number | null
  >(null)

  const finalFilters: ApplicationsFilters = {
    ...filters,
    isCompleted: false
  }

  const {
    applications,
    isApplicationsLoading,
    isApplicationsError,
    fetchNextApplicationPage,
    hasNextApplicationPage,
    isFetchingNextApplicationPage
  } = useApplications(finalFilters, !filtersLoading, 6)

  return (
    <div className={clsx(resultsWrapper, themeClass)}>
      <AnimatePresence>
        {isApplicationsError && (
          <Alert
            variation={AlertVariation.Danger}
            message={
              'Une erreur est survenue lors de la récupération des candidatures.'
            }
            onDismiss={false}
          />
        )}
        {applications?.length === 0 && (
          <NoResult
            icon={faUserGraduate}
            message="Aucune candidature"
            isSmall
          />
        )}
        {applications?.map((application) => (
          <ApplicationCard
            key={`application${application.id}`}
            application={application}
            onClick={() => setSelectedApplicationId(application.id)}
          />
        ))}
        {(isApplicationsLoading ||
          isFetchingNextApplicationPage ||
          filtersLoading) &&
          [...Array(5)].map((_, i) => (
            <Shimmer
              width="100%"
              height={71}
              // biome-ignore lint/suspicious/noArrayIndexKey: Shimmer cant have unique key
              key={i + (applications?.length ?? 0)}
            />
          ))}
        {hasNextApplicationPage && (
          <ButtonGroup align="center">
            <Button
              onClick={fetchNextApplicationPage}
              disabled={isFetchingNextApplicationPage}
              variation={ButtonVariation.Neutral}
              type="button"
              fullWidth
            >
              Charger plus de candidatures
            </Button>
          </ButtonGroup>
        )}
      </AnimatePresence>

      {selectedApplicationId &&
        createPortal(
          <ApplicationModal
            key="application-modal"
            defaultApplicationId={selectedApplicationId}
            collection={applications}
            showOrder
            onClose={() => setSelectedApplicationId(null)}
          />,
          document.querySelector('#appWrapper') as HTMLElement
        )}
    </div>
  )
}
